import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Lottie from "react-lottie";
import { useTranslation } from "i18n";

import Button from "components/button";
import { applyForJob } from "api/Jobs";
import { isUserLoggedIn } from "hooks/LocalStorage";
import animationData from "lotties/success";
import DragAndDrop from "./drag&drop-file";

const MAX_WORDS = 100;
const MIN_RECOMMENDED_WORDS = 75;

const countWords = (text = "") =>
  text
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

// Exact selection questions — do not translate or alter wording.
const APPLICATION_QUESTIONS = [
  {
    questionId: 1,
    question:
      "Waarom wil je bij Satyam Holidays werken en wat denk jij in deze functie te kunnen bijdragen?",
  },
  {
    questionId: 2,
    question:
      "Beschrijf een situatie waarin je uit eigen initiatief verantwoordelijkheid nam voor een taak, probleem of resultaat. Wat deed je en wat was het resultaat?",
  },
  {
    questionId: 3,
    question:
      "Iemand wacht op informatie, een actie of een resultaat van jou, maar je hebt nog niet alles wat je nodig hebt. Hoe zorg je voor duidelijke communicatie en tijdige opvolging?",
  },
  {
    questionId: 4,
    question:
      "Beschrijf een fout of vergissing die je hebt gemaakt. Hoe ben je ermee omgegaan en wat heb je gedaan om herhaling te voorkomen?",
  },
  {
    questionId: 5,
    question:
      "Geef een voorbeeld van iets nieuws dat je moest leren, zoals een systeem, programma, proces of werkwijze. Hoe heb je dit aangepakt en hoe controleerde je of je het goed toepaste?",
  },
  {
    questionId: 6,
    question:
      "Je hebt meerdere belangrijke taken met deadlines en een collega vraagt om hulp. Hoe bepaal je wat prioriteit heeft en welke actie je onderneemt?",
  },
];

const ApplicationStepper = ({ job, careerId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const questions = APPLICATION_QUESTIONS;

  const [phase, setPhase] = useState(0); // 0 questionnaire, 1 documents, 2 success
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [resume, setResume] = useState(null);
  const [motivationLetterFile, setMotivationLetterFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  const currentQuestion = questions[questionIndex];
  const currentAnswer = answers[currentQuestion?.questionId] || "";
  const currentWordCount = countWords(currentAnswer);

  const completedQuestions = questions.filter(
    (q) => countWords(answers[q.questionId] || "") > 0,
  ).length;

  const phaseLabels = [
    t("jobs.stepper.questionnaire"),
    t("jobs.stepper.documents"),
    t("jobs.stepper.done"),
  ];

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  useEffect(() => {
    setAnimKey((value) => value + 1);
  }, [questionIndex, phase]);

  const requireLogin = () => {
    if (isUserLoggedIn()) return true;
    setError(t("jobs.loginRequired"));
    window.location.hash = "#auth-required";
    return false;
  };

  const updateAnswer = (value) => {
    const words = countWords(value);
    if (words > MAX_WORDS) return;
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.questionId]: value,
    }));
    if (error) setError("");
  };

  const validateCurrentAnswer = () => {
    if (!currentAnswer.trim()) {
      setError(t("jobs.stepper.answerRequired"));
      return false;
    }
    if (currentWordCount > MAX_WORDS) {
      setError(t("jobs.stepper.tooManyWords", { max: MAX_WORDS }));
      return false;
    }
    return true;
  };

  const goNextQuestion = () => {
    if (!validateCurrentAnswer()) return;
    if (questionIndex < questions.length - 1) {
      setQuestionIndex((index) => index + 1);
      setError("");
      return;
    }
    setPhase(1);
    setError("");
  };

  const goPrevQuestion = () => {
    setError("");
    if (questionIndex === 0) return;
    setQuestionIndex((index) => index - 1);
  };

  const goBackToQuestions = () => {
    setPhase(0);
    setQuestionIndex(questions.length - 1);
    setError("");
  };

  const handleSubmit = async () => {
    setError("");
    if (!requireLogin()) return;

    if (!resume || !motivationLetterFile) {
      setError(t("jobs.uploadRequired"));
      return;
    }

    const missing = questions.find(
      (q) => !String(answers[q.questionId] || "").trim(),
    );
    if (missing) {
      setPhase(0);
      setQuestionIndex(questions.findIndex((q) => q.questionId === missing.questionId));
      setError(t("jobs.stepper.answerRequired"));
      return;
    }

    setLoading(true);

    const questionnaire = questions.map((q) => ({
      questionId: q.questionId,
      question: q.question,
      answer: String(answers[q.questionId] || "").trim(),
    }));

    const body = {
      careerId,
      title: job?.title,
      department: job?.department,
      location: job?.location,
      type: job?.type,
      questionnaire,
    };

    const response = await applyForJob({
      body,
      resume,
      motivationLetterFile,
    });

    setLoading(false);

    if (!response.ok) {
      if (response.status === 401) {
        setError(t("jobs.loginRequired"));
        window.location.hash = "#auth-required";
        return;
      }
      setError(response.data?.message || t("jobs.applyFailed"));
      return;
    }

    setPhase(2);
  };

  const wordMeterClass =
    currentWordCount > MAX_WORDS
      ? "is-over"
      : currentWordCount >= MIN_RECOMMENDED_WORDS
        ? "is-good"
        : currentWordCount > 0
          ? "is-low"
          : "";

  return (
    <div className="apply-flow">
      <div className="apply-flow__header">
        <h2>{t("jobs.applyNow")}</h2>
        <p>{t("jobs.applyHint")}</p>
      </div>

      <Stepper activeStep={phase} alternativeLabel className="apply-flow__stepper">
        {phaseLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {phase === 0 && (
        <div key={`q-${animKey}`} className="apply-flow__card apply-flow__card--enter">
          <div className="apply-flow__progress">
            <div className="apply-flow__progress-top">
              <span>
                {t("jobs.stepper.questionOf", {
                  current: questionIndex + 1,
                  total: questions.length,
                })}
              </span>
              <span>
                {t("jobs.stepper.answeredCount", {
                  count: completedQuestions,
                  total: questions.length,
                })}
              </span>
            </div>
            <div className="apply-flow__progress-track">
              <div
                className="apply-flow__progress-fill"
                style={{
                  width: `${((questionIndex + 1) / questions.length) * 100}%`,
                }}
              />
            </div>
          </div>

          <p className="apply-flow__guideline">{t("jobs.stepper.guideline")}</p>

          <div className="apply-flow__question-pills">
            {questions.map((q, index) => {
              const answered = countWords(answers[q.questionId] || "") > 0;
              return (
                <button
                  key={q.questionId}
                  type="button"
                  className={`apply-flow__pill${
                    index === questionIndex ? " is-active" : ""
                  }${answered ? " is-done" : ""}`}
                  onClick={() => {
                    setQuestionIndex(index);
                    setError("");
                  }}
                  aria-label={`Question ${q.questionId}`}
                >
                  {answered && index !== questionIndex ? (
                    <CheckRoundedIcon fontSize="inherit" />
                  ) : (
                    q.questionId
                  )}
                </button>
              );
            })}
          </div>

          <h3 className="apply-flow__question">{currentQuestion.question}</h3>

          <textarea
            className="apply-flow__textarea"
            value={currentAnswer}
            onChange={(e) => updateAnswer(e.target.value)}
            placeholder={t("jobs.stepper.answerPlaceholder")}
            rows={7}
          />

          <div className={`apply-flow__word-meter ${wordMeterClass}`}>
            <div className="apply-flow__word-meter-bar">
              <span
                style={{
                  width: `${Math.min((currentWordCount / MAX_WORDS) * 100, 100)}%`,
                }}
              />
            </div>
            <p>
              {t("jobs.stepper.wordCount", {
                count: currentWordCount,
                min: MIN_RECOMMENDED_WORDS,
                max: MAX_WORDS,
              })}
            </p>
          </div>

          <div className="apply-flow__actions">
            <button
              type="button"
              className="apply-flow__secondary"
              onClick={goPrevQuestion}
              disabled={questionIndex === 0}
            >
              <ArrowBackRoundedIcon fontSize="small" />
              {t("jobs.stepper.back")}
            </button>
            <Button className="primary-color apply-flow__primary" onClick={goNextQuestion}>
              {questionIndex === questions.length - 1
                ? t("jobs.stepper.continueToDocuments")
                : t("jobs.stepper.next")}
              <ArrowForwardRoundedIcon fontSize="small" />
            </Button>
          </div>
        </div>
      )}

      {phase === 1 && (
        <div key={`d-${animKey}`} className="apply-flow__card apply-flow__card--enter">
          <h3 className="apply-flow__question">{t("jobs.stepper.uploadTitle")}</h3>
          <p className="apply-flow__guideline">{t("jobs.stepper.uploadHint")}</p>

          <DragAndDrop
            file={resume}
            label={t("jobs.uploadResume")}
            getFile={setResume}
          />
          <DragAndDrop
            file={motivationLetterFile}
            label={t("jobs.uploadMotivation")}
            getFile={setMotivationLetterFile}
          />

          <div className="apply-flow__actions">
            <button
              type="button"
              className="apply-flow__secondary"
              onClick={goBackToQuestions}
            >
              <ArrowBackRoundedIcon fontSize="small" />
              {t("jobs.stepper.back")}
            </button>
            <Button
              className="primary-color apply-flow__primary"
              onClick={handleSubmit}
              loading={loading}
            >
              {t("jobs.stepper.submitApplication")}
            </Button>
          </div>
        </div>
      )}

      {phase === 2 && (
        <div key={`s-${animKey}`} className="apply-flow__success apply-flow__card--enter">
          <Lottie options={defaultOptions} height={140} width={140} />
          <h3>{t("jobs.applicationSuccess")}</h3>
          <p>{t("jobs.stepper.successHint")}</p>
          {/* Temporarily hidden — keep for later
          <Button
            className="primary-color apply-flow__primary"
            onClick={() => {
              navigate("/how-we-hire");
              window.scrollTo(0, 0);
            }}
          >
            {t("jobs.stepper.seeHiringProcess")}
          </Button>
          */}
        </div>
      )}
    </div>
  );
};

export default ApplicationStepper;
